import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Lightweight modal. The style guide ships a Radix version; this build of
 * DreamTrials does not yet include @radix-ui/react-dialog, so we use a plain
 * overlay + close-on-Escape. Swap to Radix if you add the dep.
 *
 * Rendered into document.body (16 Oct 2026). Inline, a modal opened from a
 * sticky page header (e.g. the subject status menu) was trapped inside it:
 * the header's backdrop-blur makes it the containing block for `fixed`
 * children, so the overlay covered only the header strip and the dialog was
 * centred on it, cut off above the top of the window.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  width = 720,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  title?: string;
  description?: string;
  children: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-text-1/40 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
        aria-hidden
      />
      <div
        className="relative bg-white rounded-2xl shadow-modal border border-border-soft max-h-[90vh] overflow-hidden flex flex-col"
        style={{ width: `min(${width}px, calc(100vw - 32px))` }}
      >
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-border-soft">
          <div>
            {title && <div className="font-bold text-base text-text-1">{title}</div>}
            {description && <div className="text-xs text-text-3 mt-1">{description}</div>}
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-text-4 hover:text-text-2"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-auto px-6 py-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
